// AI-assisted image processing notice (EU AI Act Art. 50 transparency).
//
// Warehouse workers are visually supported via the Live API (Gemini family).
// Before the camera or the Live API connection is used, the app must show a
// brief notice that AI-assisted image processing is in use and that no
// performance-related or biometric data is collected.
//
// Acknowledgement is tracked in sessionStorage (scoped to this tab) and is
// explicitly cleared on logout, so a new operator signing in on a shared
// device sees the notice again before the next session's camera/Live API use.

import { els } from "./dom.js";
import { t } from "./i18n.js";

const STORAGE_KEY = "vta.ai_notice_ack";

// sessionStorage is unavailable in some test/SSR environments; fall back to
// this in-memory flag so the notice still gates correctly for the tab's
// lifetime even when storage access throws.
let acknowledgedInMemory = false;

function readAcknowledged() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function writeAcknowledged(value) {
  try {
    if (value) sessionStorage.setItem(STORAGE_KEY, "1");
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    /* private mode / no storage — the in-memory flag above still holds */
  }
}

export function hasAcknowledgedAiNotice() {
  return acknowledgedInMemory || readAcknowledged();
}

// Called on logout so the next sign-in (possibly a different operator on a
// shared device) sees the notice again before any camera/Live API use.
export function resetAiNoticeAcknowledgement() {
  acknowledgedInMemory = false;
  writeAcknowledged(false);
}

export function applyAiNoticeI18n() {
  if (els.aiNoticeTitle) els.aiNoticeTitle.textContent = t("ai_notice.title");
  if (els.aiNoticeBody) els.aiNoticeBody.textContent = t("ai_notice.body");
  if (els.aiNoticeAckBtn) els.aiNoticeAckBtn.textContent = t("ai_notice.ack");
}

// Shows the overlay and resolves once the user acknowledges it. Resolves
// immediately if the overlay markup is missing (e.g. a minimal test DOM)
// rather than blocking forever.
export function showAiNotice() {
  if (!els.aiNoticeOverlay || !els.aiNoticeAckBtn) return Promise.resolve();
  applyAiNoticeI18n();
  els.aiNoticeOverlay.hidden = false;
  els.aiNoticeOverlay.dataset.state = "visible";
  return new Promise((resolve) => {
    const onAck = () => {
      acknowledgedInMemory = true;
      writeAcknowledged(true);
      els.aiNoticeOverlay.hidden = true;
      els.aiNoticeOverlay.dataset.state = "hidden";
      els.aiNoticeAckBtn.removeEventListener("click", onAck);
      resolve();
    };
    els.aiNoticeAckBtn.addEventListener("click", onAck);
  });
}

// Awaits acknowledgement the first time this session; a no-op afterwards.
// Call this before starting the camera or the Live API connection.
export async function ensureAiNoticeAcknowledged() {
  if (hasAcknowledgedAiNotice()) return;
  await showAiNotice();
}
